import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { UserProfileDto } from '@api/types/auth'
import { tokenStorage } from '@services/tokenStorage'

interface AuthState {
  // State
  isAuthenticated: boolean
  user: UserProfileDto | null
  token: string | null
  mfaRequired: boolean
  mfaToken: string | null

  // Actions
  login: (token: string, refreshToken: string, user: UserProfileDto, expiresAt: string, refreshTokenExpiresAt: string) => void
  setMfaRequired: (required: boolean, mfaToken?: string) => void
  logout: () => void
  updateUser: (user: Partial<UserProfileDto>) => void
  setToken: (token: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      // Initial state
      isAuthenticated: false,
      user: null,
      token: null,
      mfaRequired: false,
      mfaToken: null,

      // Login action
      login: (token, refreshToken, user, expiresAt, refreshTokenExpiresAt) => {
        // Store tokens securely
        tokenStorage.setTokens({
          accessToken: token,
          refreshToken,
          expiresAt,
          refreshTokenExpiresAt,
        })

        set({
          isAuthenticated: true,
          token,
          user,
          mfaRequired: false,
          mfaToken: null,
        })
      },

      // MFA required
      setMfaRequired: (required, mfaToken) => {
        set({
          mfaRequired: required,
          mfaToken: mfaToken || null,
        })
      },

      // Logout action
      logout: () => {
        tokenStorage.clearTokens()
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          mfaRequired: false,
          mfaToken: null,
        })
      },

      // Update user profile
      updateUser: user => {
        set(state => ({
          user: state.user ? { ...state.user, ...user } : null,
        }))
      },

      // Set token (for refresh)
      setToken: token => {
        set({ token })
      },
    }),
    {
      name: 'adventurecore-auth',
      partialize: state => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    }
  )
)
